import { ExchangeType } from "@redotech/redo-return-core-common/exchange-settings";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type {
  PortalSettingsPatch,
  ReturnsPortalSettingsSource,
} from "./portal-settings-source";

/**
 * How exchange orders inherit the original order's discounts. One merchant
 * choice spanning five stored fields, so the portal presents it as one row.
 */
export enum DiscountLogic {
  NONE = "none",
  REAPPLY_CODE = "reapply_code",
  REAPPLY_AMOUNT = "reapply_amount",
  SHOP_NOW_BONUS = "shop_now_bonus",
}

export const pricingSettingsShape = {
  pricing: z.object({
    discountLogic: createEnum(DiscountLogic),
    applyOriginalOrderDiscounts: z.array(createEnum(ExchangeType)),
    applyAutomaticDiscounts: z.array(createEnum(ExchangeType)),
    applyShopOnSiteCartDiscounts: z.boolean(),
    recreateExpiredDiscounts: z.boolean(),
    recreateAutomaticDiscountsAsCodes: z.boolean(),
    reapplyCompareAtDiscount: z.boolean(),
    allowCustomerDiscountCodes: z.boolean(),
    differentPricedVariantExchanges: z.boolean(),
    excludeDifferentPriceVariants: z.boolean(),
    variantExchangeProductTags: z.array(z.string()),
  }),
};

export type PricingSettings = z.infer<z.ZodObject<typeof pricingSettingsShape>>;

/**
 * One stored field per branch, checked in the order the customer portal reads
 * them: an amount reapply wins over a Shop Now bonus, which wins over codes.
 */
function discountLogicFromSettings(exchanges: {
  reapplyDiscountAmount?: boolean | null;
  usePreDiscountPrice?: boolean | null;
  applyOriginalOrderDiscounts?: ExchangeType[] | null;
  applyAutomaticDiscounts?: ExchangeType[] | null;
}): DiscountLogic {
  if (exchanges.reapplyDiscountAmount) {
    return DiscountLogic.REAPPLY_AMOUNT;
  }
  if (exchanges.usePreDiscountPrice) {
    return DiscountLogic.SHOP_NOW_BONUS;
  }
  const codes =
    (exchanges.applyOriginalOrderDiscounts?.length ?? 0) > 0 ||
    (exchanges.applyAutomaticDiscounts?.length ?? 0) > 0;
  return codes ? DiscountLogic.REAPPLY_CODE : DiscountLogic.NONE;
}

export function pricingSettingsFromTeam({
  settings,
}: ReturnsPortalSettingsSource): PricingSettings {
  const { exchanges } = settings;
  return {
    pricing: {
      discountLogic: discountLogicFromSettings(exchanges),
      applyOriginalOrderDiscounts: exchanges.applyOriginalOrderDiscounts ?? [],
      applyAutomaticDiscounts: exchanges.applyAutomaticDiscounts ?? [],
      applyShopOnSiteCartDiscounts: exchanges.shopSiteUseCartDiscounts ?? true,
      recreateExpiredDiscounts: exchanges.recreateExpiredDiscounts,
      recreateAutomaticDiscountsAsCodes:
        exchanges.recreateAutomaticDiscountsAsCodes,
      reapplyCompareAtDiscount: exchanges.reapplyCompareAtDiscount ?? false,
      allowCustomerDiscountCodes: exchanges.allowCustomerDiscountCodes ?? true,
      differentPricedVariantExchanges:
        exchanges.differentPricedVariantExchanges,
      excludeDifferentPriceVariants:
        exchanges.excludeDifferentPriceVariants ?? false,
      variantExchangeProductTags: exchanges.variantExchangeProductTags ?? [],
    },
  };
}

export function pricingTeamPatch({
  pricing,
}: PricingSettings): PortalSettingsPatch {
  // Everything under "reapply discount codes" is stored as its own field, and
  // the portal reads them independently. Clear them together when the merchant
  // picks another method, or a stale sub-toggle keeps applying.
  const reapplyCodes = pricing.discountLogic === DiscountLogic.REAPPLY_CODE;
  return {
    set: {
      "exchanges.reapplyDiscountAmount":
        pricing.discountLogic === DiscountLogic.REAPPLY_AMOUNT,
      "exchanges.usePreDiscountPrice":
        pricing.discountLogic === DiscountLogic.SHOP_NOW_BONUS,
      "exchanges.blockDiscountsOnExchanges": !reapplyCodes,
      "exchanges.applyOriginalOrderDiscounts": reapplyCodes
        ? pricing.applyOriginalOrderDiscounts
        : [],
      "exchanges.applyAutomaticDiscounts": reapplyCodes
        ? pricing.applyAutomaticDiscounts
        : [],
      "exchanges.shopSiteUseCartDiscounts":
        reapplyCodes && pricing.applyShopOnSiteCartDiscounts,
      "exchanges.recreateExpiredDiscounts":
        reapplyCodes && pricing.recreateExpiredDiscounts,
      "exchanges.recreateAutomaticDiscountsAsCodes":
        reapplyCodes &&
        pricing.recreateExpiredDiscounts &&
        pricing.recreateAutomaticDiscountsAsCodes,
      "exchanges.reapplyCompareAtDiscount":
        pricing.discountLogic !== DiscountLogic.NONE &&
        pricing.reapplyCompareAtDiscount,
      "exchanges.allowCustomerDiscountCodes":
        pricing.allowCustomerDiscountCodes,
      "exchanges.differentPricedVariantExchanges":
        pricing.differentPricedVariantExchanges,
      "exchanges.excludeDifferentPriceVariants":
        pricing.excludeDifferentPriceVariants,
      "exchanges.variantExchangeProductTags":
        pricing.variantExchangeProductTags,
    },
    unset: [],
  };
}
